import { useState, useEffect, useCallback } from "react";
import { displayDate, todayISO } from "@/lib/utils";
import { useNavigate, useSearchParams } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { receivableService } from "@/services/receivableService";
import { buyerService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, {
  getDefaultDateRange,
} from "@/components/common/DateRangeFilter";
import type { Receivable, Receipt, Buyer, BuyerBalance } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import RecordCashModal from "@/components/finance/RecordCashModal";
import Pagination from "@/components/ui/pagination/Pagination";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

type Tab = "invoices" | "balances";

const statusColor: Record<string, "success" | "warning" | "error"> = {
  paid: "success",
  partial: "warning",
  unpaid: "error",
};

const methodLabels: Record<string, string> = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

const saleTypeLabels: Record<string, string> = {
  egg: "Egg Sale",
  bird: "Bird Sale",
  manure: "Manure Sale",
  bag: "Bag Sale",
};

export default function ReceivablesPage() {
  const navigate = useNavigate();
  const isOwner = useIsOwner();

  const [tab, setTab] = useState<Tab>("invoices");
  const { sortConfig, requestSort } = useSortable("date", "desc");

  // Invoices state
  const [entries, setEntries] = useState<Receivable[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 100;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Buyers
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [showRecordReceipt, setShowRecordReceipt] = useState(false);

  // Filters — an alert can deep-link with ?status=unpaid; when it does, start from
  // an all-time range so the (older) overdue invoices it points at are actually
  // visible instead of being hidden by the default recent-days window.
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get("status") || "";
  const defaults = getDefaultDateRange();
  const [dateFrom, setDateFrom] = useState(urlStatus ? "" : defaults.from);
  const [dateTo, setDateTo] = useState(urlStatus ? "" : defaults.to);
  const [filterBuyer, setFilterBuyer] = useState("");
  const [filterStatus, setFilterStatus] = useState(urlStatus);
  const [filterSaleType, setFilterSaleType] = useState("");

  // Balances
  const [balances, setBalances] = useState<BuyerBalance[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(false);

  // Create/edit invoice modal
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formBuyerId, setFormBuyerId] = useState("");
  const [formSaleType, setFormSaleType] = useState("egg");
  const [formSaleId, setFormSaleId] = useState("");
  const [formDate, setFormDate] = useState(todayISO());
  const [formDueDate, setFormDueDate] = useState("");
  const [formInvoiceNumber, setFormInvoiceNumber] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Receipt history modal
  const [showHistory, setShowHistory] = useState(false);
  const [historyReceivable, setHistoryReceivable] = useState<Receivable | null>(null);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, string | number> = { limit, offset };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (filterBuyer) params.buyer_id = parseInt(filterBuyer);
      if (filterStatus) params.status = filterStatus;
      if (filterSaleType) params.sale_type = filterSaleType;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await receivableService.list(params);
      setEntries(res.data ?? []);
      setTotal(res.meta?.total ?? 0);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterBuyer, filterStatus, filterSaleType, offset, sortConfig]);

  const fetchBalances = useCallback(async () => {
    setBalancesLoading(true);
    setError("");
    try {
      const data = await receivableService.getBalances();
      setBalances(data ?? []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  useEffect(() => {
    buyerService.list().then(setBuyers).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "invoices") fetchInvoices();
    else fetchBalances();
  }, [tab, fetchInvoices, fetchBalances]);

  const handleDateApply = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setOffset(0);
  };

  const handlePrev = () => {
    setOffset((prev) => Math.max(0, prev - limit));
  };

  const handleNext = () => {
    setOffset((prev) => prev + limit);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormBuyerId("");
    setFormSaleType("egg");
    setFormSaleId("");
    setFormDate(todayISO());
    setFormDueDate("");
    setFormInvoiceNumber("");
    setFormAmount("");
    setFormNotes("");
  };

  const openEdit = (r: Receivable) => {
    setEditingId(r.id);
    setFormBuyerId(String(r.buyer_id));
    setFormSaleType(r.sale_type);
    setFormSaleId(String(r.sale_id));
    setFormDate(r.date?.toString().slice(0, 10) ?? todayISO());
    setFormDueDate(r.due_date?.toString().slice(0, 10) ?? "");
    setFormInvoiceNumber(r.invoice_number || "");
    setFormAmount(String(r.total_amount));
    setFormNotes(r.notes || "");
    setError("");
    setShowCreate(true);
  };

  const handleSaveInvoice = async () => {
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await receivableService.update(editingId, {
          buyer_id: parseInt(formBuyerId),
          date: formDate,
          due_date: formDueDate || undefined,
          invoice_number: formInvoiceNumber || undefined,
          total_amount: parseFloat(formAmount),
          notes: formNotes || undefined,
        });
      } else {
        // Unreachable while "+ New Invoice" is hidden — kept so the button can
        // come back without rebuilding the form.
        await receivableService.create({
          buyer_id: parseInt(formBuyerId),
          sale_type: formSaleType,
          sale_id: parseInt(formSaleId),
          date: formDate,
          due_date: formDueDate || undefined,
          invoice_number: formInvoiceNumber || undefined,
          total_amount: parseFloat(formAmount),
          notes: formNotes || undefined,
        });
      }
      setShowCreate(false);
      resetForm();
      fetchInvoices();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openReceiptHistory = async (receivable: Receivable) => {
    setError(""); // don't carry a stale error from another modal into this one
    setHistoryReceivable(receivable);
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const data = await receivableService.listReceipts(receivable.id);
      setReceipts(data ?? []);
    } catch {
      // silently fail
    } finally {
      setHistoryLoading(false);
    }
  };

  const totalOutstanding = entries
    .filter((e) => e.status !== "paid")
    .reduce((sum, e) => sum + (e.total_amount - e.received_amount), 0);

  return (
    <>
      <PageMeta title="Accounts Receivable | Poultry Book" description="Track buyer invoices and receipts" />
      <PageBreadcrumb pageTitle="Accounts Receivable" />

      {/* Tabs */}
      <div className="mb-4 flex gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700 w-fit">
        {([["invoices", "Invoices"], ["balances", "Buyer Balances"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === key
                ? "bg-brand-500 text-white"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && !showCreate && !showHistory && (
        <div className="mb-4 rounded-lg bg-error-50 px-4 py-3 text-sm text-error-600 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </div>
      )}

      {/* ---- INVOICES TAB ---- */}
      {tab === "invoices" && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <DateRangeFilter onApply={handleDateApply} />
              <select value={filterBuyer} onChange={(e) => setFilterBuyer(e.target.value)} className={selectClass + " !w-auto !h-8 !px-2 !py-0 !text-xs"}>
                <option value="">All Buyers</option>
                {buyers.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectClass + " !w-auto !h-8 !px-2 !py-0 !text-xs"}>
                <option value="">All Status</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
              <select value={filterSaleType} onChange={(e) => setFilterSaleType(e.target.value)} className={selectClass + " !w-auto !h-8 !px-2 !py-0 !text-xs"}>
                <option value="">All Types</option>
                <option value="egg">Egg Sale</option>
                <option value="bird">Bird Sale</option>
                <option value="manure">Manure Sale</option>
                <option value="bag">Bag Sale</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              {totalOutstanding > 0 && (
                <span className="text-sm font-medium text-success-600 dark:text-success-400">
                  Receivable: {"\u20B9"}{totalOutstanding.toLocaleString("en-IN")}
                </span>
              )}
              {isOwner && (
                <Button size="sm" variant="outline" onClick={() => setShowRecordReceipt(true)}>
                  Record Receipt
                </Button>
              )}
              {/* "+ New Invoice" is hidden: every sale raises its own invoice,
                  and a hand-typed one could bill a sale twice (P0-4). The
                  server refuses a second invoice for a sale either way. */}
            </div>
          </div>

          <div className="overflow-hidden card">
            {loading ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
            ) : entries.length === 0 && !error ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">No invoices in the selected period</div>
            ) : entries.length === 0 ? null : (
              <div className="max-w-full overflow-x-auto">
                <Table padded>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Buyer" sortKey="buyer" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Type" sortKey="sale_type" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Invoice #" sortKey="invoice_number" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Received" sortKey="received" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Outstanding" sortKey="outstanding" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Due Date" sortKey="due_date" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                      <TableCell isHeader>Actions</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((r) => {
                      const outstanding = r.total_amount - r.received_amount;
                      const isOverdue = r.due_date && r.status !== "paid" && new Date(r.due_date) < new Date();
                      return (
                        <TableRow key={r.id}>
                          <TableCell>{displayDate(r.date)}</TableCell>
                          <TableCell>{r.buyer_name}</TableCell>
                          <TableCell>{saleTypeLabels[r.sale_type] ?? r.sale_type}</TableCell>
                          <TableCell>{r.invoice_number || "-"}</TableCell>
                          <TableCell>{"\u20B9"}{r.total_amount.toLocaleString("en-IN")}</TableCell>
                          <TableCell>{"\u20B9"}{r.received_amount.toLocaleString("en-IN")}</TableCell>
                          <TableCell className={outstanding > 0 ? "font-medium text-success-600 dark:text-success-400" : ""}>
                            {"\u20B9"}{outstanding.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell>
                            {r.due_date ? (
                              <span className={isOverdue ? "text-error-600 dark:text-error-400 font-medium" : ""}>
                                {displayDate(r.due_date)}
                                {isOverdue && " (overdue)"}
                              </span>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge color={statusColor[r.status] ?? "light"}>{r.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openReceiptHistory(r)}
                                className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
                              >
                                History
                              </button>
                              {isOwner && (
                                <button
                                  onClick={() => openEdit(r)}
                                  className="text-xs font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
                                >
                                  Edit
                                </button>
                              )}
                              {/* No Delete: every invoice is raised by a sale,
                                  and deleting it alone left the sale with no
                                  revenue in the books. Delete the sale instead. */}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
          </div>
        </>
      )}

      {/* ---- BALANCES TAB ---- */}
      {tab === "balances" && (
        <div className="overflow-hidden card">
          {balancesLoading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : balances.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">No buyer balances</div>
          ) : balances.length === 0 ? null : (
            <div className="max-w-full overflow-x-auto">
              <Table padded>
                <TableHeader>
                  <TableRow>
                    <TableCell isHeader>Buyer</TableCell>
                    <TableCell isHeader>Total Invoiced</TableCell>
                    <TableCell isHeader>Total Received</TableCell>
                    <TableCell isHeader>Outstanding</TableCell>
                    <TableCell isHeader>Credit</TableCell>
                    <TableCell isHeader>Overdue Invoices</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.map((b) => (
                    <TableRow key={b.buyer_id}>
                      <TableCell className="font-medium">
                        <button onClick={() => navigate(`/receivables/buyer/${b.buyer_id}`)} className="text-brand-500 hover:text-brand-600 hover:underline dark:text-brand-400">
                          {b.buyer_name}
                        </button>
                      </TableCell>
                      <TableCell>{"\u20B9"}{b.total_invoiced.toLocaleString("en-IN")}</TableCell>
                      <TableCell>{"\u20B9"}{b.total_received.toLocaleString("en-IN")}</TableCell>
                      <TableCell className={b.outstanding > 0 ? "font-medium text-success-600 dark:text-success-400" : ""}>
                        {"\u20B9"}{b.outstanding.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell className={b.credit > 0 ? "font-medium text-brand-600 dark:text-brand-400" : "text-gray-400"}>
                        {b.credit > 0 ? `\u20B9${b.credit.toLocaleString("en-IN")}` : "-"}
                      </TableCell>
                      <TableCell>
                        {b.overdue_count > 0 ? (
                          <Badge color="error">{b.overdue_count} overdue</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className="font-bold">Total</TableCell>
                    <TableCell className="font-bold">{"\u20B9"}{balances.reduce((s, b) => s + b.total_invoiced, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold">{"\u20B9"}{balances.reduce((s, b) => s + b.total_received, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold text-success-600 dark:text-success-400">{"\u20B9"}{balances.reduce((s, b) => s + b.outstanding, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="font-bold text-brand-600 dark:text-brand-400">{"\u20B9"}{balances.reduce((s, b) => s + b.credit, 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell>{""}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ---- CREATE / EDIT INVOICE MODAL ---- */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} className="max-w-md">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
            {editingId ? "Edit Invoice" : "New Invoice"}
          </h3>
          {error && showCreate && (
            <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label>Buyer *</Label>
              <select value={formBuyerId} onChange={(e) => setFormBuyerId(e.target.value)} className={selectClass}>
                <option value="">Select buyer</option>
                {buyers.filter((b) => b.status === "active").map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sale Type *</Label>
                <select value={formSaleType} onChange={(e) => setFormSaleType(e.target.value)} className={selectClass} disabled={!!editingId}>
                  <option value="egg">Egg Sale</option>
                  <option value="bird">Bird Sale</option>
                  <option value="manure">Manure Sale</option>
                  <option value="bag">Bag Sale</option>
                </select>
              </div>
              <div>
                <Label>Sale ID *</Label>
                <Input type="number" value={formSaleId} onChange={(e) => setFormSaleId(e.target.value)} placeholder="Sale record ID" disabled={!!editingId} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Invoice Date *</Label>
                <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={formDueDate} onChange={(e) => setFormDueDate(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Invoice Number</Label>
                <Input type="text" value={formInvoiceNumber} onChange={(e) => setFormInvoiceNumber(e.target.value)} placeholder="INV-001" />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input type="number" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} min="0.01" step="0.01" placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input type="text" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleSaveInvoice} disabled={saving || !formBuyerId || !formDate || !formAmount || !formSaleId}>
              {saving ? "Saving..." : editingId ? "Update Invoice" : "Create Invoice"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ---- RECEIPT HISTORY MODAL ---- */}
      <Modal isOpen={showHistory} onClose={() => setShowHistory(false)} className="max-w-lg">
        <div className="p-6">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">Receipt History</h3>
          {error && showHistory && (
            <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
              {error}
            </div>
          )}
          {historyReceivable && (
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              {historyReceivable.buyer_name} — Invoice {historyReceivable.invoice_number || `#${historyReceivable.id}`}
              {" "}({"\u20B9"}{historyReceivable.total_amount.toLocaleString("en-IN")})
            </p>
          )}
          {historyLoading ? (
            <p className="py-4 text-center text-sm text-gray-400">Loading...</p>
          ) : receipts.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">No receipts recorded yet.</p>
          ) : (
            <div className="max-w-full overflow-x-auto">
              <Table padded>
                <TableHeader>
                  <TableRow>
                    <TableCell isHeader>Date</TableCell>
                    <TableCell isHeader>Amount</TableCell>
                    <TableCell isHeader>Method</TableCell>
                    <TableCell isHeader>Reference</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.map((rc) => (
                    <TableRow key={rc.id}>
                      <TableCell>{displayDate(rc.date)}</TableCell>
                      <TableCell>{"\u20B9"}{rc.amount.toLocaleString("en-IN")}</TableCell>
                      <TableCell>{methodLabels[rc.method] ?? rc.method}</TableCell>
                      <TableCell>{rc.reference || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </div>
        </div>
      </Modal>

      <RecordCashModal
        isOpen={showRecordReceipt}
        onClose={() => setShowRecordReceipt(false)}
        kind="receipt"
        parties={buyers.filter((x) => x.status === "active").map((x) => ({ id: x.id, name: x.name }))}
        onSubmit={async ({ party_id, ...rest }) =>
          (await receivableService.recordBuyerReceipt({ buyer_id: party_id, ...rest })).settled
        }
        onSaved={() => {
          fetchInvoices();
          fetchBalances();
        }}
      />
    </>
  );
}
